/**
 * <personalization-fields>: progressive enhancement for the personalization
 * block. Without this script the inputs still submit with the product form
 * (maxlength and required are native). This adds live remaining-character
 * counts, an image preview, client-side file type/size checks and a visible
 * message when a required field is empty.
 */
if (!customElements.get('personalization-fields')) {
  customElements.define(
    'personalization-fields',
    class PersonalizationFields extends HTMLElement {
      connectedCallback() {
        this.maxBytes = (parseFloat(this.dataset.maxMb) || 20) * 1024 * 1024;
        this.errorEl = this.querySelector('[data-pz-error]');

        this.querySelectorAll('[maxlength]').forEach((input) => {
          const counter = this.querySelector(`[data-count-for="${input.id}"]`);
          if (!counter) return;
          const update = () => {
            counter.textContent = counter.dataset.template.replace('[[n]]', String(input.maxLength - input.value.length));
          };
          input.addEventListener('input', update);
          update();
        });

        this.querySelectorAll('input[type="file"]').forEach((input) => {
          input.addEventListener('change', () => this.onFileChange(input));
        });

        if (this.dataset.previewFields) this.initPreview();

        const formId = this.querySelector('[form]')?.getAttribute('form');
        this.form = formId ? document.getElementById(formId) : null;
        if (this.form) {
          // Capture phase so this runs before product-form.js posts the cart request.
          this.form.addEventListener('submit', (event) => this.onSubmit(event), true);
        }
      }

      // Feature 011: live text preview over the main image (contracts/pdp-ui.md §5). Text only, via textContent.
      initPreview() {
        const slot = this.closest('product-info')?.querySelector('[data-personalization-preview]');
        if (!slot) return;
        const keys = this.dataset.previewFields.split(',').map((key) => key.trim()).filter(Boolean);
        const inputs = keys.map((key) => this.querySelector(`[data-pz-key="${CSS.escape(key)}"]`)).filter(Boolean);
        if (!inputs.length) return;
        const make = (className, text) => {
          const span = document.createElement('span');
          span.className = className;
          span.textContent = text;
          return span;
        };
        const label = make('product-gallery__preview-label', this.dataset.previewLabel || '');
        const text = make('product-gallery__preview-text', '');
        slot.replaceChildren(label, text);
        if (this.dataset.previewCaption) slot.append(make('product-gallery__preview-caption', this.dataset.previewCaption));
        const update = () => {
          text.textContent = inputs
            .map((input) => (input.value || '').trim() || input.getAttribute('placeholder') || '')
            .filter(Boolean)
            .join(this.dataset.previewSeparator || ' · ');
        };
        inputs.forEach((input) => {
          input.addEventListener('input', update);
          input.addEventListener('change', update);
        });
        update();
        slot.hidden = false;
      }

      onSubmit(event) {
        if (this.validateRequired()) {
          this.clearError();
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(this.dataset.msgMissing);
      }

      validateRequired() {
        let valid = true;
        this.querySelectorAll('[required]').forEach((field) => {
          const empty = field.type === 'file' ? field.files.length === 0 : field.value.trim() === '';
          field.setAttribute('aria-invalid', empty ? 'true' : 'false');
          if (empty && valid) {
            valid = false;
            field.focus();
          }
        });
        return valid;
      }

      onFileChange(input) {
        const preview = this.querySelector(`[data-preview-for="${input.id}"]`);
        const file = input.files && input.files[0];
        this.setDropzoneLabel(input, false);
        if (!file) {
          this.clearPreview(preview);
          return;
        }
        const typeOk = /^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
        if (!typeOk) {
          input.value = '';
          this.clearPreview(preview);
          this.showError(this.dataset.msgWrongType);
          return;
        }
        if (file.size > this.maxBytes) {
          input.value = '';
          this.clearPreview(preview);
          this.showError(this.dataset.msgTooLarge);
          return;
        }
        this.clearError();
        this.renderPreview(preview, input, file);
        this.setDropzoneLabel(input, true);
      }

      // Feature 031: the dropzone pill reads "Change photo" while a photo is chosen
      setDropzoneLabel(input, chosen) {
        const button = this.querySelector(`.personalization__dropzone[for="${input.id}"] [data-dropzone-button]`);
        if (!button) return;
        button.textContent = chosen ? button.dataset.changeLabel : button.dataset.uploadLabel;
      }

      renderPreview(preview, input, file) {
        if (!preview) return;
        this.clearPreview(preview);
        const url = URL.createObjectURL(file);
        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        img.addEventListener('load', () => URL.revokeObjectURL(url));
        const name = document.createElement('span');
        name.className = 'personalization__preview-name';
        name.textContent = file.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'personalization__remove';
        remove.textContent = this.dataset.removeLabel || 'Remove';
        remove.addEventListener('click', () => {
          input.value = '';
          this.clearPreview(preview);
          this.setDropzoneLabel(input, false);
          input.focus();
        });
        preview.append(img, name, remove);
        preview.hidden = false;
      }

      clearPreview(preview) {
        if (!preview) return;
        preview.replaceChildren();
        preview.hidden = true;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      clearError() {
        this.showError('');
      }
    }
  );
}
